  import { useNavigate, useParams } from "react-router";
  import useApiQuery from "../../../hooks/useApiQuery";
  import { formatDate } from "../../../utilities";
  import BillingDetails from "../../cart/BillingDetails";
  import { Box, Typography } from "@mui/material";
  import Grid from "@mui/material/Grid2";
  import { getOrderDetails } from "../../../utilities/service";
  import OrderCard from "../../my-orders/OrderCard";
  import StepIndicator from "../../my-orders/order-details/StepIndicator";
  import ProductDetails from "../../my-orders/order-details/ProductDetails";
  import AddressInformation from "../../my-orders/order-details/AddressInformation";
  import { ProfileSkeleton } from "../../my-orders/ProfileSkeleton";
  import NoOrdersFound from "../../my-orders/NoOrdersFound";

  function OrderDetails() {
    const navigate = useNavigate();

    // Get orderId from URL params (path param)
    const { orderId } = useParams();

    const {
      data: orderDetails,
      isLoading,
    } = useApiQuery({
      queryKey: ["getOrderDetails", orderId],
      queryFn: () => getOrderDetails(orderId),
    });

    const formattedDate = formatDate(orderDetails?.expected_delivery);

    const billingDetailsProps = {
      billDetails: {
        addonTotalAmount: +orderDetails?.order_price_details?.addon_total_amount,
        cakeSubTotalAfterDiscount:
          +orderDetails?.order_price_details?.cake_subtotal_discount,
        cakeTotalAmount: +orderDetails?.order_price_details?.cake_total_amount,
        cartSubTotal: +orderDetails?.order_price_details?.cake_subtotal,
        gstAmount: +orderDetails?.order_price_details?.gst_amount,
        gstSlab: orderDetails?.order_price_details?.gst_slab_details?.map(
          (item) => ({
            cgstPercent: item?.cgst_percent,
            sgstPercent: item?.sgst_percent,
            cgst: +item?.cgst,
            sgst: +item?.sgst,
          })
        ),
        taxableAmount: +orderDetails?.order_price_details?.taxable_amount,
        totalAmount: +orderDetails?.order_price_details?.total_amount,
        dynamicCouponSave: +orderDetails?.promo_code_value || 0,
        bulkSubTotal: +orderDetails?.order_price_details?.bulk_subtotal || 0,
        productDiscountSave: +orderDetails?.product_discount_value || 0,
      },
      deliveryFeeData: {
        rate: +orderDetails?.actual_delivery_fee,
        free_delivery: +orderDetails?.deliver_fee_type,
      },
      deliveryType:
        orderDetails?.delivery_mode == "Pick Up"
          ? "PICK_UP"
          : "DOOR_STEP_DELIVERY",
      promo: {
        code: orderDetails?.promo_code,
        percentage: orderDetails?.promo_code_percentage,
        type: orderDetails?.promo_type,
        save: +orderDetails?.promo_code_value,
      },
      fromOrderDetails: true,
    };

  const BillingInformation = () => {
    if (!orderDetails?.ordered_products) {
      return (
        <Box>
          <BillingDetails
            itemCount={{ cart: 0, addon: 0, bulk: 0 }}
            {...billingDetailsProps}
          />
        </Box>
      );
    }

    // Get unique parent combo IDs
    const parentComboIds = new Set();
    const regularProducts = [];
    let bulkCount = 0; // ✅ add this

    orderDetails.ordered_products.forEach((product) => {
      if (product.parent_product_details?.[0]?.id) {
        parentComboIds.add(product.parent_product_details[0].id);
      } else if (Number(product.combo_price) === 0 && !product.parent_product_details?.[0]) {
        // ✅ Check if it's a bulk product
        if (product?.is_bulk_product > 0) {
          bulkCount++;
        } else {
          regularProducts.push(product);
        }
      }
    });

    const cartItemCount = parentComboIds.size + regularProducts.length;

    return (
      <Box>
        <BillingDetails
          itemCount={{
            cart: cartItemCount,
            addon: orderDetails?.ordered_addons?.length || 0,
            bulk: bulkCount,
          }}
          {...billingDetailsProps}
        />
      </Box>
    );
  };

    // Show order list if no orderId in URL
    if (isLoading) return <ProfileSkeleton />;
    if (!isLoading && !orderDetails) return <NoOrdersFound />;

    return (
      <Box>
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            fontWeight: 700,
            fontSize: { xs: "20px", sm: "24px" },
            lineHeight: "150%",
            color: "var(--neutral-black)",
            textAlign: "left",
            px: { xs: 2, sm: 0 },
          }}
        >
          Order Details
        </Typography>
        <Grid container spacing={{ xs: 2, sm: 3, md: 5 }}>
          <Grid size={{ xs: 12, md: 8 }} sx={{ pt: { xs: 2, md: 3 } }}>
            <Box sx={{ px: { xs: 1, sm: 0 } }}>
              <OrderCard
                orderId={orderDetails?.order_id}
                deliveryDate={formattedDate}
                label={
                  orderDetails?.delivery_mode == "Pick Up"
                    ? "Pickup target"
                    : "Delivery target"
                }
                deliveryMode={orderDetails?.delivery_mode}
                slot={orderDetails?.slot_timing}
              />
              <Box sx={{ mt: { xs: 3, md: 4 }, mb: 2 }}>
                <Grid container spacing={{ xs: 2, md: 2 }} sx={{ width: "100%" }}>
                  <Grid size={12}>
                    <ProductDetails data={orderDetails} />
                  </Grid>
                  <Grid size={12}>
                    <AddressInformation data={orderDetails} />
                  </Grid>
                  <Grid size={12}>
                    <BillingInformation />
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Box sx={{ px: { xs: 2, sm: 0 }, mt: { xs: 2, md: 0 } }}>
              <StepIndicator orderStatus={orderDetails} />
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  }

  export default OrderDetails;